import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShieldAlert, AlertTriangle } from 'lucide-react';

export default function AnnouncementPopup({ isOpen, onClose }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 overflow-y-auto">
          {/* Backdrop with blur */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-xs transition-opacity"
          />

          {/* Modal Container */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            transition={{ type: 'spring', duration: 0.5 }}
            className="relative w-full max-w-xl my-8 overflow-hidden rounded-2xl bg-white shadow-2xl border border-slate-200/80 z-10"
          >
            {/* Top decorative accent bar */}
            <div className="h-2.5 bg-gradient-to-r from-[#4b717a] to-[#3d5c64] w-full" />

            {/* Close button (top right) */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-[#4b717a] cursor-pointer"
              aria-label="Luk vindue"
            >
              <X className="h-5 w-5" />
            </button>

            {/* Content */}
            <div className="p-6 sm:p-8">
              {/* Header Badges */}
              <div className="flex flex-wrap items-center gap-2 mb-3">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-semibold text-red-700 ring-1 ring-inset ring-red-200">
                  <ShieldAlert className="h-3.5 w-3.5" />
                  Advarsel
                </span>
              </div>

              {/* Title */}
              <h3 className="text-xl sm:text-2xl font-semibold text-slate-900 mb-4 leading-snug pr-8">
                Falske sms’er med MinLæge som afsender
              </h3>

              <div className="space-y-4 text-sm sm:text-base leading-relaxed text-slate-700">
                <p>
                  Der er i øjeblikket svindlere, der sender falske sms’er ud til patienter med MinLæge som afsender. Både Sundhed.dk og PLO er orienteret om dette phishing-forsøg.
                </p>

                <div className="flex flex-col sm:flex-row gap-4 items-start">
                  <div className="rounded-xl bg-red-50 border border-red-200 p-4 text-sm flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-4.5 w-4.5 text-red-600" />
                      <h4 className="font-semibold text-slate-900">Vores opfordring til dig</h4>
                    </div>
                    <ul className="list-disc pl-5 space-y-1.5 text-slate-700">
                      <li>Klik aldrig på links i sms’er.</li>
                      <li>Afgiv aldrig personlige oplysninger eller betalingsoplysninger.</li>
                      <li>MinLæge-appen vil aldrig bede om dine betalingskortoplysninger.</li>
                      <li>Der opkræves aldrig betaling for ydelser i MinLæge-appen.</li>
                    </ul>
                  </div>

                  <figure className="w-32 shrink-0 mx-auto sm:mx-0">
                    <a href="/_assets/image_62314caa.png" target="_blank" rel="noopener noreferrer">
                      <img
                        src="/cdn-cgi/image/width=300,quality=80,format=auto/_assets/image_62314caa.png"
                        alt="Eksempel på falsk sms med MinLæge som afsender, der beder om betaling af konsultationsgebyr"
                        className="w-full rounded-lg border border-slate-200 shadow-sm"
                      />
                    </a>
                    <figcaption className="mt-1.5 text-xs text-slate-500 text-center">Eksempel på falsk sms</figcaption>
                  </figure>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-t border-slate-100 pt-4">
                <span className="text-sm font-medium text-slate-700">
                  Venlig hilsen <br className="hidden sm:inline" />
                  <span className="font-semibold text-slate-900">Hellerup Lægehus</span>
                </span>
                <button
                  onClick={onClose}
                  className="w-full sm:w-auto inline-flex justify-center items-center rounded-xl bg-[#4b717a] hover:bg-[#3d5c64] px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition-all cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#4b717a] focus:ring-offset-2"
                >
                  Forstået, luk besked
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
